import { Link } from "react-router-dom";
import { Linkedin, Mail, Phone, MapPin } from "lucide-react";
import { COMPANY, NAV_LINKS } from "@/config/company";

const SERVICE_LINKS = [
  { label: "ISO 27001 Consulting", to: "/services#iso-27001" },
  { label: "ISO 22301 Consulting", to: "/services#iso-22301" },
  { label: "ISO 9001 Consulting", to: "/services#iso-9001" },
  { label: "PCI DSS Consulting", to: "/services#pci-dss" },
  { label: "Cybersecurity Assessments", to: "/services#cyber-assessments" },
  { label: "SWIFT CSP Assessments", to: "/services#swift-csp" },
  { label: "VAPT", to: "/services#vapt" },
  { label: "ISO Standards Training", to: "/services#iso-training" },
];

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer className="bg-navy-900 text-slate-300" data-testid="site-footer">
      <div className="container-site py-14 sm:py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
        <div>
          <div className="mb-4">
            <span className="bg-white rounded-lg px-2.5 py-1.5 inline-flex items-center justify-center">
              <img src="/logo.png" alt="Numeric Consulting & Solutions logo" className="h-10 w-auto" />
            </span>
            <span className="block text-[11px] font-mono uppercase tracking-[0.15em] text-teal mt-3">{COMPANY.brandTagline}</span>
          </div>
          <p className="text-sm leading-relaxed text-slate-400">
            Management systems and ISO standards consulting — helping organizations strengthen governance, protect
            information assets and build operational resilience.
          </p>
          <a
            href={COMPANY.linkedin}
            data-testid="footer-linkedin-link"
            className="mt-5 inline-flex items-center gap-2 text-sm text-slate-300 hover:text-teal transition-colors"
          >
            <Linkedin size={16} aria-hidden="true" />
            LinkedIn
          </a>
        </div>
        <nav aria-label="Footer">
          <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-teal mb-4">Company</h2>
          <ul className="space-y-2.5 text-sm">
            {NAV_LINKS.map((l) => (
              <li key={l.to}>
                <Link to={l.to} data-testid={`footer-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className="hover:text-white transition-colors">
                  {l.label}
                </Link>
              </li>
            ))}
            <li>
              <Link to="/careers" data-testid="footer-nav-careers" className="hover:text-white transition-colors">Careers</Link>
            </li>
          </ul>
        </nav>
        <nav aria-label="Services">
          <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-teal mb-4">Services</h2>
          <ul className="space-y-2.5 text-sm">
            {SERVICE_LINKS.map((l) => (
              <li key={l.label}>
                <Link to={l.to} data-testid={`footer-service-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="hover:text-white transition-colors">
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
        <div>
          <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-teal mb-4">Contact</h2>
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-2.5">
              <Mail size={15} className="mt-0.5 shrink-0 text-slate-500" aria-hidden="true" />
              <span data-testid="footer-email">{COMPANY.email}</span>
            </li>
            <li className="flex items-start gap-2.5">
              <Phone size={15} className="mt-0.5 shrink-0 text-slate-500" aria-hidden="true" />
              <span data-testid="footer-phone">{COMPANY.phone}</span>
            </li>
            <li className="flex items-start gap-2.5">
              <MapPin size={15} className="mt-0.5 shrink-0 text-slate-500" aria-hidden="true" />
              <span data-testid="footer-address">{COMPANY.address}</span>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="container-site py-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-400">
          <p>© {year} {COMPANY.name}. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}
